/**
 * AnswerChips — selectable answer option chips for the RCA wizard.
 *
 * Each chip has a left border color derived from the option's multiplier
 * (1=green, 3=amber, 5=red) and shows a checkmark when selected.
 * Keyboard-accessible: Tab to focus, Enter/Space to select.
 */

import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AnswerOption } from '@/types/rca';

// ============================================================
// Helpers
// ============================================================

/** Map a multiplier (1-5) to a left border color class. */
function borderColorFromMultiplier(m: number): string {
  if (m >= 5) return 'border-l-risk-high';
  if (m === 4) return 'border-l-orange-400';
  if (m === 3) return 'border-l-risk-medium';
  if (m === 2) return 'border-l-sky-400';
  return 'border-l-risk-low';
}

/** Map a multiplier to a muted fill when selected. */
function selectedFillFromMultiplier(m: number): string {
  if (m >= 5) return 'bg-risk-high/10 border-risk-high/30';
  if (m === 4) return 'bg-orange-400/10 border-orange-400/30';
  if (m === 3) return 'bg-risk-medium/10 border-risk-medium/30';
  if (m === 2) return 'bg-sky-400/10 border-sky-400/30';
  return 'bg-risk-low/10 border-risk-low/30';
}

// ============================================================
// Types
// ============================================================

interface AnswerChipsProps {
  options: AnswerOption[];
  selected: number | 'NA' | null;
  onSelect: (value: number | 'NA') => void;
  allowNA?: boolean;
}

// ============================================================
// Component
// ============================================================

export function AnswerChips({
  options,
  selected,
  onSelect,
  allowNA = false,
}: AnswerChipsProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
      {options.map((option, idx) => {
        const isSelected = selected === idx;
        const borderCls = borderColorFromMultiplier(option.m);
        const selectedCls = selectedFillFromMultiplier(option.m);

        return (
          <button
            key={option.l}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(idx)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(idx);
              }
            }}
            className={cn(
              // Base chip
              'group relative flex items-start gap-3 rounded-lg border bg-card p-4',
              'border-l-4 text-left transition-all duration-150 cursor-pointer',
              'hover:scale-[1.02] hover:shadow-medium focus-visible:outline-none',
              'focus-visible:ring-2 focus-visible:ring-cot-blue focus-visible:ring-offset-2',
              borderCls,
              isSelected ? selectedCls : 'border-border hover:border-cot-blue/40',
            )}
          >
            {/* Option letter badge */}
            <span
              className={cn(
                'flex-shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center',
                'text-xs font-bold border transition-colors duration-150',
                isSelected
                  ? 'bg-cot-blue border-cot-blue text-white'
                  : 'border-muted-foreground/40 text-muted-foreground group-hover:border-cot-blue/60',
              )}
            >
              {isSelected ? (
                <Check className="w-3 h-3" strokeWidth={3} />
              ) : (
                option.l.toUpperCase()
              )}
            </span>

            {/* Option text */}
            <span
              className={cn(
                'text-sm leading-snug transition-colors duration-150',
                isSelected ? 'text-foreground font-medium' : 'text-foreground/80',
              )}
            >
              {option.t}
            </span>
          </button>
        );
      })}

      {/* Not Applicable chip */}
      {allowNA && (
        <button
          type="button"
          role="radio"
          aria-checked={selected === 'NA'}
          onClick={() => onSelect('NA')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect('NA');
            }
          }}
          className={cn(
            'group relative flex items-start gap-3 rounded-lg border bg-card p-4',
            'border-l-4 text-left transition-all duration-150 cursor-pointer',
            'hover:scale-[1.02] hover:shadow-medium focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-cot-blue focus-visible:ring-offset-2',
            'border-l-gray-400',
            selected === 'NA'
              ? 'bg-gray-100/80 border-gray-300 dark:bg-gray-800/50 dark:border-gray-600'
              : 'border-border hover:border-gray-400/60',
          )}
        >
          <span
            className={cn(
              'flex-shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center',
              'text-xs font-bold border transition-colors duration-150',
              selected === 'NA'
                ? 'bg-gray-500 border-gray-500 text-white'
                : 'border-muted-foreground/40 text-muted-foreground',
            )}
          >
            {selected === 'NA' ? <Check className="w-3 h-3" strokeWidth={3} /> : 'N'}
          </span>
          <span
            className={cn(
              'text-sm leading-snug transition-colors duration-150',
              selected === 'NA' ? 'text-foreground font-medium' : 'text-muted-foreground',
            )}
          >
            Not Applicable
          </span>
        </button>
      )}
    </div>
  );
}
